// AI Generated Component
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { HeatmapApiService } from '@client/app/services/entities/heatmaps/heatmaps.service';
import { HeatmapsDialogsPagesComponent } from '@client/app/modules/heatmaps/dialogs/pages/pages.component';

@Component({
    selector: 'heatmaps-dashboard',
    standalone: false,
    template: `
        <div class="heatmaps-dashboard">
            <page-header
                [icon]="['MENUES', 'HEATMAPS'] | monIcons"
                [header]="'<div class=\\'pull-left\\'>' + ('Heatmaps' | translate) + '</div>'"
                [subHeader]="subHeader">
                <page-header-content>
                    <mon-switch-button
                        [defaultIndex]="1"
                        *ngIf="rules.total < 100"
                        class="mr-2 text-right"
                        [label]="'Choose how to view the Heatmaps Dashboard' | translate"
                        [options]="viewOptions"
                        (onSelected)="setView($event)"
                    ></mon-switch-button>
                </page-header-content>
            </page-header>

            <heatmaps-dashboard-table
                [rules]="rules"
                [domains]="domains"
                (getPage)="getPage()"
                [loading]="loading"
                (onPageChange)="onPageChange($event.page)"
                (onPerPageChange)="onPageSizeChange($event.size)"
                *ngIf="view === 'table'">
            </heatmaps-dashboard-table>

            <heatmaps-dashboard-grid
                [rules]="rules"
                [domains]="domains"
                (getPage)="getPage()"
                [loading]="loading"
                *ngIf="view === 'grid'">
            </heatmaps-dashboard-grid>
        </div>
    `,
})
export class HeatmapsDashboardComponent implements OnInit, OnDestroy {
    subHeader: string = '';
    loading: boolean = true;
    rules: any = [];
    search: string = '';
    view: string = 'grid';
    domains: any = [];
    page: number = 1;
    pageSize: number = 100;
    viewOptions: string[] = [];
    fetchingDomains$: Subscription = new Subscription();

    constructor (
        private translateService: TranslateService,
        private heatmapApiService: HeatmapApiService,
        private monEventService: MonEventService,
        private sessionService: SessionService,
        private totalDomainsService: TotalDomainsService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    private activate (): void {
        this.subHeader = this.translateService.getString('Monitor how visitors interact with your pages. Acquia Web Governance tracks up to a maximum of 1000 heatmaps sessions per page per month.');
        this.loading = true;
        this.rules = [];
        this.search = '';
        this.view = 'grid';
        this.domains = [];
        this.page = 1;
        this.pageSize = 100;
        this.viewOptions = [
            this.translateService.getString('Table View'),
            this.translateService.getString('Grid View'),
        ];

        this.fetchingDomains$ = this.totalDomainsService.fetchingDomains
            .subscribe((isFetching) => {
                if (!isFetching) {
                    this.getPage();
                }
            });
    }

    getPage (): void {
        this.loading = true;
        const params = {
            page: this.page,
            page_size: this.pageSize,
        };
        this.heatmapApiService.getAllFromCustomer(params)
            .then((rules) => {
                this.rules = rules;
                if (this.rules.total > 100) {
                    this.view = 'table';
                }
                this.mapDomains(rules);
            }, () => {})
            .finally(() => {
                this.loading = false;
            });
    }

    addPage (): void {
        this.openPageForm(undefined, () => {
            this.getPage();
        });
    }

    canAddMoreRules (): boolean {
        if (this.sessionService.customer) {
            return (this.sessionService.customer?.heatmaps_tracked_number_of_pages || 0) > this.rules.total;
        }
        return false;
    }

    setView (viewNumber: number): void {
        if (viewNumber === 0) {
            this.view = 'table';
        } else {
            this.view = 'grid';
        }
    }

    onPageChange (page: number): void {
        this.page = page;
        this.getPage();
    }

    onPageSizeChange (size: number): void {
        this.pageSize = size;
        this.getPage();
    }

    private onDestroy (): void {
        if (this.fetchingDomains$) {
            this.fetchingDomains$.unsubscribe();
        }
    }

    private openPageForm (rule?: any, fn?: () => void): void {
        const params = {
            body: HeatmapsDialogsPagesComponent,
            size: 'md',
            data: {
                rules: this.rules,
            },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback: fn });
    }

    private mapDomains (rules: any): void {
        rules.map((rule: any) => {
            const target = this.totalDomainsService.domainsEssential?.value?.find((domain: any) => {
                return domain.id === rule.domain_id;
            });

            if (target) {
                this.domains[rule.id] = target;
            }
        });
    }
}
